/**
 * TestModeBanner Component (Low Balance Banner)
 *
 * A clean, thin banner that appears above the prompt bar when the user's
 * balance is low or out. Works in BOTH test and production modes.
 *
 * 2026-08-14 owner directive: chat never mentions the balance unless it is
 * low or out.
 * - LOW (0 < balance <= the user's auto top-up threshold; the caller decides):
 *   one light pr0-denominated nudge, dismissible (reappears on interaction).
 * - OUT (balance <= $0, `isCritical`): explains WHY sending is unavailable;
 *   NOT dismissible — the composer is blocked until a top-up/redeem.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { usdToWu, formatWu, WU_LABEL, WU_FULL_NAME } from '../../config/workUnits';

// ============================================================================
// Styled Components
// ============================================================================

const Banner = styled.div<{ $isCritical: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  margin-bottom: 8px;
  background: ${p => p.$isCritical ? '#FEF2F2' : '#FFFBEB'};
  border: 1px solid ${p => p.$isCritical ? '#FECACA' : '#FDE68A'};
  border-radius: 8px;
  font-size: 0.8125rem;
  color: ${p => p.$isCritical ? '#991B1B' : '#92400E'};
`;

const Badge = styled.span<{ $isCritical: boolean }>`
  background: ${p => p.$isCritical ? '#FEE2E2' : '#FEF3C7'};
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: lowercase;
  letter-spacing: 0.5px;
`;

const Message = styled.span`
  flex: 1;
`;

const ActionButton = styled.button<{ $isCritical: boolean }>`
  background: ${p => p.$isCritical ? '#DC2626' : '#D97706'};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: inherit;
  opacity: 0.4;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// ============================================================================
// Component Props
// ============================================================================

interface TestModeBannerProps {
  /** Current balance in USD */
  balanceUsd: number;
  /** Whether the wallet is OUT (<= $0) — changes styling/messaging and disables dismissal */
  isCritical: boolean;
  /** Whether system is in testing mode (optional, for backward compatibility) */
  isTestingMode?: boolean;
  /** Whether banner should be shown (balance is below warning threshold) */
  shouldShow: boolean;
  /** Whether user has a payment method on file */
  hasPaymentMethod: boolean;
  /** Callback when user clicks the action button */
  onNavigate: () => void;
  /** Key that changes on every interaction - resets dismissed state */
  interactionKey: string | number;
}

// ============================================================================
// Component
// ============================================================================

export const TestModeBanner: React.FC<TestModeBannerProps> = ({
  balanceUsd,
  isCritical,
  isTestingMode,
  shouldShow,
  hasPaymentMethod,
  onNavigate,
  interactionKey,
}) => {
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state on EVERY interaction (new message, chat open/close, etc.)
  useEffect(() => {
    setDismissed(false);
  }, [interactionKey]);

  // Don't render if shouldn't show, or dismissed (dismissal only ever applies
  // to the LOW nudge — the OUT state is not dismissible).
  // Shows in BOTH test and production modes.
  if (!shouldShow || (dismissed && !isCritical)) {
    return null;
  }

  const message = isCritical
    ? `you're out of ${WU_FULL_NAME} — that's why sending is unavailable. top up or redeem a code to resume instantly.`
    : `balance running low — ${formatWu(usdToWu(balanceUsd))} ${WU_LABEL} left`;
  const buttonText = hasPaymentMethod ? 'top up' : 'add payment method';

  return (
    <Banner $isCritical={isCritical}>
      <Badge $isCritical={isCritical}>{isCritical ? 'OUT' : 'LOW'}</Badge>
      <Message>{message}</Message>
      <ActionButton $isCritical={isCritical} onClick={onNavigate}>
        {buttonText}
      </ActionButton>
      {!isCritical && (
        <DismissButton onClick={() => setDismissed(true)} title="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </DismissButton>
      )}
    </Banner>
  );
};

export default TestModeBanner;
